import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Check, Loader2, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { submitFeedback } from "../../api/feedback";
import { useChatSession } from "../../context/ChatSessionProvider";
import { ActionButton } from "./ActionButton";

const FEEDBACK_TAGS = [
  "Factually incorrect",
  "Wrong citations",
  "Hallucinations",
  "Incomplete answer",
  "Outdated information",
];

export const FeedbackButtons = () => {
  const { sessionId } = useChatSession();
  const [positiveSubmitted, setPositiveSubmitted] = useState(false);
  const [negativeSubmitted, setNegativeSubmitted] = useState(false);
  const [isSubmittingPositive, setIsSubmittingPositive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmittingNegative, setIsSubmittingNegative] = useState(false);

  // Reset state when session changes
  useEffect(() => {
    setPositiveSubmitted(false);
    setNegativeSubmitted(false);
    setModalOpen(false);
    setFeedbackText("");
    setSelectedTags([]);
  }, [sessionId]);

  const handlePositive = useCallback(async () => {
    if (!sessionId || isSubmittingPositive) return;
    setIsSubmittingPositive(true);
    try {
      await submitFeedback({
        session_id: sessionId,
        is_high_quality_answer: true,
      });
      setPositiveSubmitted(true);
      setTimeout(() => setPositiveSubmitted(false), 2000);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
    } finally {
      setIsSubmittingPositive(false);
    }
  }, [sessionId, isSubmittingPositive]);

  const handleNegativeSubmit = useCallback(async () => {
    if (!sessionId || isSubmittingNegative) return;
    setIsSubmittingNegative(true);
    try {
      await submitFeedback({
        session_id: sessionId,
        is_high_quality_answer: false,
        feedback_text: feedbackText || null,
        tags: selectedTags.length > 0 ? selectedTags : null,
      });
      setNegativeSubmitted(true);
      setModalOpen(false);
      setFeedbackText("");
      setSelectedTags([]);
      setTimeout(() => setNegativeSubmitted(false), 2000);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
    } finally {
      setIsSubmittingNegative(false);
    }
  }, [sessionId, isSubmittingNegative, feedbackText, selectedTags]);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }, []);

  const canSubmit = selectedTags.length > 0 || feedbackText.trim().length > 0;

  return (
    <>
      <ActionButton
        label={positiveSubmitted ? "Feedback submitted" : "Helpful response"}
        onClick={handlePositive}
        disabled={isSubmittingPositive || positiveSubmitted}
      >
        {isSubmittingPositive ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : positiveSubmitted ? (
          <Check className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <ThumbsUp className="h-3.5 w-3.5" />
        )}
      </ActionButton>

      <ActionButton
        label={negativeSubmitted ? "Feedback submitted" : "Not helpful"}
        onClick={() => setModalOpen(true)}
        disabled={isSubmittingNegative || negativeSubmitted}
      >
        {negativeSubmitted ? (
          <Check className="h-3.5 w-3.5 text-green-500" />
        ) : (
          <ThumbsDown className="h-3.5 w-3.5" />
        )}
      </ActionButton>

      <Dialog open={modalOpen} onOpenChange={(open) => !open && setModalOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Tell us what went wrong</DialogTitle>
            <DialogDescription>
              Help us improve by sharing what was inaccurate or incomplete.
            </DialogDescription>
          </DialogHeader>

          <div className="pt-2">
            {selectedTags.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {selectedTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-1 text-xs font-medium text-red-700"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => toggleTag(tag)}
                      aria-label={`Remove tag ${tag}`}
                      title={`Remove tag ${tag}`}
                      className="rounded-full p-0.5 transition-colors hover:bg-red-200"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <label htmlFor="feedback-text" className="sr-only">
              Additional feedback
            </label>
            <textarea
              id="feedback-text"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="h-24 w-full resize-none rounded border border-gray-300 bg-white p-2.5 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-amber-500"
              autoFocus
            />

            <div className="mt-2">
              <span className="mr-2 text-xs text-gray-500">Quick tags:</span>
              <div className="mt-1 inline-flex flex-wrap gap-1.5">
                {FEEDBACK_TAGS.filter((t) => !selectedTags.includes(t)).map(
                  (tag) => (
                    <button
                      key={tag}
                      type="button"
                      aria-label={`Add tag ${tag}`}
                      onClick={() => toggleTag(tag)}
                      className="leading-normal rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 transition-colors hover:border-gray-400 hover:bg-gray-50"
                    >
                      {tag}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-end">
            <Button
              onClick={() => setModalOpen(false)}
              disabled={isSubmittingNegative}
              variant="outline"
              size="sm"
            >
              <X className="h-3 w-3" />
              Cancel
            </Button>
            <Button
              onClick={handleNegativeSubmit}
              disabled={isSubmittingNegative || !canSubmit}
              variant="outline"
              size="sm"
            >
              <Send className="h-3 w-3" />
              {isSubmittingNegative ? "Submitting..." : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
